/**
 * Whether Chrome/Edge's built-in page translator has rewritten the document.
 *
 * The browser stamps `translated-ltr` / `translated-rtl` on `<html>` while a
 * translation is applied. That is an undocumented implementation detail, so this
 * is deliberately BEST-EFFORT and must stay that way: use it for diagnostics
 * only, never to gate behaviour or user-facing copy. If the browser renames the
 * class we lose a Sentry tag and nothing else.
 *
 * Why we care: in-page translation replaces text nodes with `<font>`-wrapped
 * copies, which re-parents them. React keeps the original text node as a fiber's
 * `stateNode`, so removing a subtree whose root is a bare text node throws
 * `NotFoundError: The node to be removed is not a child of this node`. When an
 * error boundary catches that, knowing whether the page was translated is the
 * difference between a five-minute diagnosis and a week of guessing.
 *
 * The structural defence is to never leave bare text as the root of a
 * conditional subtree — see `LibraryContentHeaderLeft`.
 */
export function isPageTranslated(): boolean {
  if (typeof document === 'undefined') return false;
  const { classList } = document.documentElement;
  return classList.contains('translated-ltr') || classList.contains('translated-rtl');
}

/**
 * How many `<font>` elements a translator has injected inside the React root.
 *
 * This is the direct fingerprint, and it is worth more than `isPageTranslated()` on a
 * crash report: it does not depend on an undocumented class name, and it measures the
 * thing that actually breaks React — text nodes that have been given a new parent —
 * rather than merely that a translation was requested. A `removeChild` failure
 * arriving alongside a count in the hundreds needs no further diagnosis.
 *
 * `<font>` is deprecated and appears nowhere in this codebase, so any hit is foreign.
 * Returns 0 rather than throwing when there is no root to inspect, so it is safe to
 * call from `beforeSend` during early startup.
 */
export function countTranslatorFontWrappers(): number {
  if (typeof document === 'undefined') return 0;
  // Live HTMLCollection — reading `.length` is cheap enough for an error path.
  return document.getElementById('root')?.getElementsByTagName('font').length ?? 0;
}
